import api from "../config/api";
import { ServiceReturn } from "../types/ServiceResponse";

export interface UploadedFileReference {
  id?: string;
  fileId: string;
  storageKey: string;
  thumbnailKey?: string | null;
  uploadStatus?: string | null;
  mimeType: string;
  fileName: string;
  originalFileName?: string | null;
  size: number;
  extension?: string | null;
  sha256?: string | null;
  durationSeconds?: number | null;
  pageCount?: number | null;
  width?: number | null;
  height?: number | null;
  url?: string | null;
  thumbnailUrl?: string | null;
}

export interface MultipartUploadInitiatePayload {
  fileName: string;
  mimeType: string;
  size: number;
  context: string;
  ownerId?: string | null;
  metadata?: Record<string, any>;
}

export interface MultipartUploadInitiateData {
  fileId: string;
  uploadId: string;
  storageKey: string;
  partSize: number;
  totalParts: number;
  expiresAt: string;
}

export interface MultipartUploadPresignPayload {
  fileId: string;
  uploadId: string;
  parts: number[];
}

export interface MultipartUploadPresignData {
  parts: Array<{
    partNumber: number;
    url: string;
    expiresAt: string;
  }>;
}

export interface MultipartUploadCompletePayload {
  fileId: string;
  uploadId: string;
  sha256?: string | null;
  parts: Array<{
    partNumber: number;
    etag: string;
  }>;
}

export interface MultipartUploadAbortPayload {
  fileId: string;
  uploadId: string;
}

export interface MultipartUploadStatusData {
  fileId: string;
  uploadStatus: string;
  uploadedParts?: Array<{
    partNumber: number;
    etag: string;
  }>;
  totalParts?: number;
  expiresAt?: string;
}

const moduleBaseUrl = "file";

export const initiateMultipartUpload = async (
  payload: MultipartUploadInitiatePayload,
  token?: string
): Promise<ServiceReturn<MultipartUploadInitiateData>> => {
  try {
    const { data } = await api.post(`/${moduleBaseUrl}/multipart/initiate`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return data;
  } catch (error: any) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const presignMultipartUploadParts = async (
  payload: MultipartUploadPresignPayload,
  token?: string
): Promise<ServiceReturn<MultipartUploadPresignData>> => {
  try {
    const { data } = await api.post(`/${moduleBaseUrl}/multipart/presign-parts`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return data;
  } catch (error: any) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const completeMultipartUpload = async (
  payload: MultipartUploadCompletePayload,
  token?: string
): Promise<ServiceReturn<UploadedFileReference>> => {
  try {
    const { data } = await api.post(`/${moduleBaseUrl}/multipart/complete`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return data;
  } catch (error: any) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const abortMultipartUpload = async (
  payload: MultipartUploadAbortPayload,
  token?: string
): Promise<ServiceReturn<boolean>> => {
  try {
    const { data } = await api.post(`/${moduleBaseUrl}/multipart/abort`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return data;
  } catch (error: any) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const getMultipartUploadStatus = async (
  fileId: string,
  token?: string
): Promise<ServiceReturn<MultipartUploadStatusData>> => {
  try {
    const { data } = await api.get(`/${moduleBaseUrl}/multipart/${fileId}/status`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return data;
  } catch (error: any) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const presignFileDownload = async (
  payload: {
    fileMetadataId?: string;
    fileId?: string;
    storageKey?: string;
    disposition?: "inline" | "attachment";
    expiresInSeconds?: number;
  },
  token?: string
): Promise<ServiceReturn<{ url: string; expiresAt: string }>> => {
  try {
    const { data } = await api.post(`/${moduleBaseUrl}/presign-download`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return data;
  } catch (error: any) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};
